import * as models from "./models";
import MockAdapter from "axios-mock-adapter";
import {AxiosInstance, AxiosRequestConfig} from "axios";

const ERROR_MODE = false;

function parseParamFromConfig(cfg: AxiosRequestConfig, position: number): string | null {
    const path = cfg.url;
    if (!path) {
        return null;
    }
    const pathParts = path.split("/");
    return pathParts[position];
}

function mockError() {
    return [200, "OK"];
}

function mockInvitationValidate() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
                errors: {
                    inviteCode: ["Incorrect invite code"],
                    zipCode: ["Incorrect zip code"],
                },
            },
        ];
    }
    return [
        200,
        {
            status: "active",
            urlCode: "123ab",
            applicationInfo: {
                firstName: "Bob",
                middleInitial: "B",
                lastName: "Bobbers",
                addressLine1: "123 Bob Street",
                addressLine2: "#301",
                city: "Bobville",
                state: "TX",
                zipCode: "12345",
                bindingID: "1234567",
                invitationCode: "123456789",
                cardTypes: ["BLACK_VISA", "GOLD_VISA", "PHOTOCARD_VISA", "BLUE_VISA", "TEAL_VISA"],
                // cardTypes: ["PHOTOCARD_MC"],
                attributes: {
                    multiImageSlider: true,
                    defaultCardType: "GOLD_VISA",
                },
            },
        },
    ];
    // return [
    //     200,
    //     {
    //         status: "too-many-attempts",
    //         urlCode: "123ab",
    //         applicationInfo: null,
    //     },
    // ];
}

function mockApplicationLeadSuccess() {
    return [200, "OK"];
}

function mockApplicationValidate() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["This form is invalid.", "This combination of fields is not allowed."],
                errors: {
                    [models.AppKeys.AccountTotal]: ["This field is invalid."],
                    [models.AddressValidationKeys.AddressLine1]: ["address line 1 is invalid."],
                    [models.AddressValidationKeys.AddressLine2]: ["address line 2 is invalid."],
                    [models.AddressValidationKeys.City]: ["city is invalid."],
                    [models.AddressValidationKeys.State]: ["state is invalid."],
                    [models.AddressValidationKeys.Zipcode]: ["zipcode is invalid."],
                    [models.AppKeys.AnnualGrossIncome]: ["This field is invalid."],
                    [models.AppKeys.DateOfBirth]: ["This field is invalid."],
                    [models.AppKeys.Email]: ["This field is invalid."],
                    [models.AppKeys.FirstName]: ["This field is invalid."],
                    [models.AppKeys.LastName]: ["This field is invalid."],
                    [models.AppKeys.MiddleInitial]: ["This field is invalid."],
                    [models.AppKeys.AnnualObligation]: ["This field is invalid."],
                    [models.AppKeys.PaymentAccount]: ["This field is invalid."],
                    [models.AppKeys.PrimaryPhone]: ["This field is invalid."],
                    [models.AppKeys.Residency]: ["This field is invalid."],
                    [models.AppKeys.SocialSecurity]: ["This field is invalid."],
                    [models.AppKeys.StudentStatus]: ["This field is invalid."],
                    [models.AppKeys.CheckingAccount]: ["Checking account is invalid."],
                    [models.AppKeys.SavingsAccount]: ["Savings account is invalid."],
                    [models.AppKeys.College]: ["Invalid College Message."],
                },
            },
        ];
    }
    return [200, "OK"];
}

const APPROVED_APP: models.ApplicationSubmitResponse = {
    status: models.ApplicationStatus.Approved,
    counterOffer: null,
    applicationId: "abc123",
    creditLine: 4000,
    addressInfo: {
        addressLine1: "123 Circle Rd",
        addressLine2: null,
        city: "Racoon City",
        state: "TX",
        zipCode: "12345",
    },
    firstName: "Bob",
    middleInitial: null,
    lastName: "McBobbers",
    econsent: true,
    email: "BobMcBobbers@umbrellacorp.com",
    homePhone: "5555555555",
    instantCard: false,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DUPLICATE_APP: models.ApplicationSubmitResponse = {
    status: models.ApplicationStatus.Duplicate,
    instantCard: false,
    counterOffer: null,
    applicationId: null,
    creditLine: null,
    addressInfo: null,
    firstName: null,
    middleInitial: null,
    lastName: null,
    econsent: false,
    email: null,
    homePhone: null,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DUPLICATE_DSN_APP: models.ApplicationSubmitResponse = {
    status: models.ApplicationStatus.DuplicateDsn,
    instantCard: false,
    counterOffer: null,
    applicationId: null,
    creditLine: null,
    addressInfo: null,
    firstName: null,
    middleInitial: null,
    lastName: null,
    econsent: false,
    email: null,
    homePhone: null,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SECURED_APP: models.ApplicationSubmitResponse = {
    status: models.ApplicationStatus.Secured,
    instantCard: false,
    counterOffer: {
        type: "secured",
        apr: 20.0,
        annualFee: 55.0,
        mpd: 15.0,
        expDate: new Date("2021-05-31"),
        depositMin: "50.0",
        depositMax: "250.0",
        depositStep: "50.0",
    },
    applicationId: "abc123",
    creditLine: null,
    addressInfo: null,
    firstName: "Bob",
    middleInitial: null,
    lastName: "McBobbers",
    econsent: true,
    email: "BobMcBobbers@umbrellacorp.com",
    homePhone: "5555555555",
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SECURED_APP_ACCEPTED: models.ApplicationSubmitResponse = {
    status: models.ApplicationStatus.SecuredAccepted,
    instantCard: false,
    counterOffer: {
        type: "secured",
        apr: 20.0,
        annualFee: 55.0,
        mpd: 15.0,
        expDate: new Date("2021-05-31"),
        depositMin: "50.0",
        depositMax: "250.0",
        depositStep: "50.0",
    },
    applicationId: "abc123",
    creditLine: null,
    addressInfo: null,
    firstName: "Bob",
    middleInitial: null,
    lastName: "McBobbers",
    econsent: true,
    email: "BobMcBobbers@umbrellacorp.com",
    homePhone: "5555555555",
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const INSTANT_CARD_APP: models.ApplicationSubmitResponse = {
    status: models.ApplicationStatus.Approved,
    instantCard: true,
    counterOffer: null,
    applicationId: "abc123",
    creditLine: 4000,
    addressInfo: {
        addressLine1: "123 Circle Rd",
        addressLine2: null,
        city: "Racoon City",
        state: "TX",
        zipCode: "12345",
    },
    firstName: "Bob",
    middleInitial: null,
    lastName: "McBobbers",
    econsent: true,
    email: "BobMcBobbers@umbrellacorp.com",
    homePhone: "5555555555",
};

const INSTANT_CARD: models.InstantCard = {
    accountNumber: "5555444433332222",
    expirationDate: "2025-01",
    creditLimit: 150,
    cvv: "123",
};

function mockApplicationSubmit() {
    return [200, APPROVED_APP];
    // return [200, {...APPROVED_APP, status: models.ApplicationStatus.Processing}];
    // return [200, {...APPROVED_APP, status: models.ApplicationStatus.ThankYou}];
    // return [200, DUPLICATE_APP];
    // return [200, DUPLICATE_DSN_APP];
    // return [200, SECURED_APP];
    // return [200, INSTANT_CARD_APP];
}

function mockApplicationSecurity() {
    if (ERROR_MODE) {
        return [
            400,
            {
                errors: {
                    [models.AppSecurityKeys.MothersMaidenName]: ["This field is invalid."],
                    [models.AppSecurityKeys.ContactName]: ["This field is invalid 2."],
                    [models.AppSecurityKeys.ContactRelationship]: ["This field is invalid 3."],
                    [models.AppSecurityKeys.ContactAddress]: ["This field is invalid 4."],
                    [models.AppSecurityKeys.ContactHomePhone]: ["This field is invalid 5."],
                    [models.AppSecurityKeys.ContactPermission]: ["This field is invalid 6."],
                },
            },
        ];
    }
    return [200, "OK"];
}

function mockGetInstantCard() {
    if (ERROR_MODE) {
        return [
            401,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }
    return [200, INSTANT_CARD];
}

const MOCK_COLLEGES = [
    "The University of Texas at Austin",
    "The University of Tennessee",
    "The University of Texas at San Antonio",
    "University of Pennsylvania",
    "South Dakota State University",
    "Rice University",
    "University of South Dakota",
];

function mockCollegeSearch(cfg: AxiosRequestConfig) {
    const search = parseParamFromConfig(cfg, 3);
    if (!search) {
        return [400];
    }
    return [200, MOCK_COLLEGES.filter((c) => c.includes(search))];
}

function mockVisit() {
    if (ERROR_MODE) {
        return [400];
    }
    return [200, "OK"];
}

function mockForgotInvite() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Unable to locate an invite code with the information provided."],
            },
        ];
    }
    return [
        200,
        {
            status: "active",
            urlCode: "123ab",
            applicationInfo: {
                firstName: "Bob",
                middleInitial: "B",
                lastName: "Bobbers",
                addressLine1: "123 Bob Street",
                addressLine2: null,
                city: "Bobville",
                state: "Texas",
                zipCode: "12345",
                bindingID: "1234567",
                invitationCode: "123456789",
                cardTypes: ["BLACK_VISA", "GOLD_VISA", "PHOTOCARD_VISA", "BLUE_VISA", "TEAL_VISA"],
                // cardTypes: ["PHOTOCARD_MC"],
                attributes: {
                    multiImageSlider: true,
                    defaultCardType: "GOLD_VISA",
                },
            },
        },
    ];
    // return [
    //     200,
    //     {
    //         status: "expired",
    //         urlCode: "123ab",
    //         applicationInfo: null,
    //     },
    // ];
}

function mockCardTypes() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }

    return [
        200,
        {
            cardTypes: ["BLACK_VISA", "GOLD_VISA", "TEAL_VISA", "BLUE_VISA", "PHOTOCARD_VISA"],
            // cardTypes: ["GOLD_MC", "PHOTOCARD_MC"],
            attributes: {
                multiImageSlider: true,
                defaultCardType: "GOLD_VISA",
            },
        },
    ];
}

function mockReview() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Unable to locate an invite code with the information provided."],
                errors: {
                    [models.ReviewKeys.Text]: ["Text Invalid"],
                    [models.ReviewKeys.Title]: ["Title Invalid"],
                    [models.ReviewKeys.Email]: ["Email Invalid"],
                },
            },
        ];
    } else {
        return [200, "OK"];
    }
}

function mockGetApplication() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Unable to locate application w/ the provided information."],
                errors: {
                    [models.LookupAppKeys.SsnLast4]: ["SSN is incorrect"],
                    [models.LookupAppKeys.DateOfBirth]: ["Date of birth is incorrect"],
                },
            },
        ];
    }
    return [200, SECURED_APP];
}
function mockPostSecured() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    } else {
        return [200, "OK"];
    }
}

function mockCounterOffer() {
    if (ERROR_MODE) {
        return [400];
    } else {
        return [
            200,
            {
                type: "secured",
                apr: 29.9,
                annualFee: 55.0,
                mpd: 15.0,
                expDate: new Date("2021-05-31"),
                depositMin: "100.0",
                depositMax: "250.0",
                depositStep: "50.0",
            },
        ];
    }
}

function mockSecuredBankDeposit() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
                errors: {
                    [models.SecuredDepositKeys.Amount]: ["Amount is incorrect"],
                    [models.SecuredDepositKeys.AbaNumber]: ["Routing number is incorrect"],
                    [models.SecuredDepositKeys.AccountNumber]: ["Account number is incorrect"],
                },
            },
        ];
    }
    return [200, "OK"];
}

function mockSecuredCardDeposit() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
                errors: {
                    [models.SecuredDepositKeys.Amount]: ["Amount is incorrect"],
                    [models.SecuredDepositKeys.CardName]: ["Name is incorrect"],
                    [models.AddressValidationKeys.AddressLine1]: ["address line 1 is invalid."],
                    [models.AddressValidationKeys.AddressLine2]: ["address line 2 is invalid."],
                    [models.AddressValidationKeys.City]: ["city is invalid."],
                    [models.AddressValidationKeys.State]: ["state is invalid."],
                    [models.AddressValidationKeys.Zipcode]: ["zipcode is invalid."],
                    [models.SecuredDepositKeys.CardNumber]: ["Card number is invalid."],
                    [models.SecuredDepositKeys.CardExpirationDate]: [
                        "Card expiration is incorrect",
                    ],
                },
            },
        ];
    }
    return [200, "OK"];
}

function mockSecuredCardReminder() {
    if (ERROR_MODE) {
        return [
            400,
            {
                errors: {
                    date: [
                        "A reminder cannot be created for that date. Please select another date.",
                    ],
                },
            },
        ];
    }
    return [200, "OK"];
}

function mockViewedReferAFriend() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }
    return [
        200,
        {
            shortCode: "sc1243",
        },
    ];
}

function mockReferAFriendDecision() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }
    return [200, "OK"];
}

function mockReferAFriendValidate() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }
    return [
        200,
        {
            shortCode: "sc1243",
        },
    ];
}

function mockViewedRedeemAFriend(cfg: AxiosRequestConfig) {
    const search = parseParamFromConfig(cfg, 2);
    if (ERROR_MODE || !search) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }
    return [
        200,
        {
            referrerFirstName: "James",
            recipientRewardAmount: "50",
        },
    ];
}

function mockRedeemAFriendDecision(cfg: AxiosRequestConfig) {
    const search = parseParamFromConfig(cfg, 2);
    if (ERROR_MODE || !search) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
            },
        ];
    }
    return [
        200,
        {
            referrerFirstName: "Joe",
            recipientRewardAmount: "50",
        },
    ];
}

function mockForgotSecuredCard() {
    if (ERROR_MODE) {
        return [
            400,
            {
                generic: ["Something went wrong!"],
                errors: {
                    [models.AppKeys.Email]: ["Email is incorrect!"],
                },
            },
        ];
    }
    return [200, "OK"];
}

export function setMockResponses(instance: AxiosInstance, timeoutMS: number) {
    const mock = new MockAdapter(instance, {delayResponse: timeoutMS});

    mock.onPost("/error").reply(mockError);
    mock.onPost("/invitation/validate").reply(mockInvitationValidate);
    mock.onPost("/application/lead").reply(mockApplicationLeadSuccess);
    mock.onPost("/application/validate").reply(mockApplicationValidate);
    mock.onPost("/application/submit").reply(mockApplicationSubmit);
    mock.onPost("/application/security").reply(mockApplicationSecurity);
    mock.onGet("/instant-card").reply(mockGetInstantCard);
    mock.onGet(/\/application\/collegeSearch\/.+/).reply(mockCollegeSearch);
    mock.onPost("/visit").reply(mockVisit);
    mock.onPost("/invitation/forgot").reply(mockForgotInvite);
    mock.onPost("/invitation/digitalOffer").reply(mockCardTypes);
    mock.onPost("/application/review").reply(mockReview);
    mock.onPost("/secured-card").reply(mockPostSecured);
    mock.onGet("/counter-offer").reply(mockCounterOffer);
    mock.onPost("/secured-card/bank-deposit").reply(mockSecuredBankDeposit);
    mock.onPost("/secured-card/card-deposit").reply(mockSecuredCardDeposit);
    mock.onPost("/refer-a-friend/refer/").reply(mockReferAFriendDecision);
    mock.onPost("/refer-a-friend/refer/view").reply(mockViewedReferAFriend);
    mock.onGet("/refer-a-friend/validate").reply(mockReferAFriendValidate);
    mock.onGet(/\/refer-a-friend\/redeem\/.+/).reply(mockViewedRedeemAFriend);
    mock.onPost(/\/refer-a-friend\/redeem\/.+/).reply(mockRedeemAFriendDecision);
    mock.onPost("/secured-card/reminder").reply(mockSecuredCardReminder);
    mock.onGet("/application").reply(mockGetApplication);
    mock.onPost("/application/forgot").reply(mockForgotSecuredCard);
}
